import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Facility } from "types/facility.type";
import {
  createSeparator,
  patchSeparator,
  SeparatorMetadataPayload,
  SeparatorQueryKey,
  TSeparatorMetadataSchema,
  separatorMetadataSchema,
} from "api/separator.api";
import { FacilityQueryKey } from "api/facility.api";
import {
  getScheduledMaintenanceItem,
  ScheduledMaintenanceQueryKey,
} from "api/scheduledMaintenance.api";
import { Separator, SeparatorType } from "types/separator/separator.type";
import {
  fromInitialInspectionDate,
  getNextInspectionLabel,
  hasInspectionHistory,
  toInitialInspectionDatePayload,
} from "types/separator/initialInspectionDate.helpers";

/** Status per besiktningstyp för objektet som redigeras. */
export type InspectionScheduleStatus = {
  /** Planerad, pågående eller utförd uppgift finns → datumet styrs av historiken. */
  hasHistory: boolean;
  /** "Nästa tillfälle" att visa i stället för datumväljaren. */
  nextLabel?: string;
};

export type InspectionScheduleInfo = {
  /** Statusen hämtas fortfarande (endast vid redigering). */
  isLoading: boolean;
  fiveYears: InspectionScheduleStatus;
  sixMonths: InspectionScheduleStatus;
};

const NO_HISTORY: InspectionScheduleInfo = {
  isLoading: false,
  fiveYears: { hasHistory: false },
  sixMonths: { hasHistory: false },
};

const EMPTY_MONTHS = {
  january: false,
  february: false,
  march: false,
  april: false,
  may: false,
  june: false,
  july: false,
  august: false,
  september: false,
  october: false,
  november: false,
  december: false,
};

export const useCreateOrUpdateSeparatorLogic = (
  facility?: Facility,
  onSuccess?: (s: Separator) => void,
  separatorToUpdate?: Separator,
) => {
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const form = useForm<TSeparatorMetadataSchema>({
    resolver: zodResolver(separatorMetadataSchema),
    defaultValues: {
      type: SeparatorType.OilAndSlam,
      avtalFemarskontroll: false,
      avtalHalvarsKontroll: false,
      avtalSlamtomning: false,
      initialFemarsKontrollDate: null,
      initialHalvarsKontrollDate: null,
      tomningReminder: EMPTY_MONTHS,
    },
  });

  const resetForm = useCallback(() => {
    separatorToUpdate
      ? form.reset({
          type: separatorToUpdate.type,
          identifier: separatorToUpdate.identifier,
          fritextType: separatorToUpdate.fritextType,
          notes: separatorToUpdate.notes,
          coordinates: separatorToUpdate.coordinates,
          propertyDesignation: separatorToUpdate.propertyDesignation,
          avtalFemarskontroll: separatorToUpdate.avtalFemarskontroll || false,
          avtalHalvarsKontroll: separatorToUpdate.avtalHalvarsKontroll || false,
          avtalSlamtomning: separatorToUpdate.avtalSlamtomning || false,
          initialFemarsKontrollDate: fromInitialInspectionDate(
            separatorToUpdate.initialFemarsKontrollDate,
          ),
          initialHalvarsKontrollDate: fromInitialInspectionDate(
            separatorToUpdate.initialHalvarsKontrollDate,
          ),
          tomningReminder: separatorToUpdate.tomningReminder || EMPTY_MONTHS,
        })
      : form.reset();
  }, [form, separatorToUpdate]);

  useEffect(() => {
    if (separatorToUpdate) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [separatorToUpdate]);

  // Vid redigering: finns redan uppgifter av typen för objektet? Då visas
  // nästa tillfälle i stället för fältet för första planerade datum.
  const facilityId = separatorToUpdate?.facility?.id;
  const { data: scheduleItem, isLoading: isScheduleLoading } = useQuery({
    ...getScheduledMaintenanceItem(facilityId ?? -1),
    enabled: !!separatorToUpdate && !!facilityId && isModalOpen,
  });

  const inspectionSchedule: InspectionScheduleInfo = useMemo(() => {
    if (!separatorToUpdate) {
      return NO_HISTORY;
    }
    if (!scheduleItem) {
      return { ...NO_HISTORY, isLoading: isScheduleLoading };
    }
    const dto = scheduleItem.separatorDtoList.find(
      (x) => x.separator.id === separatorToUpdate.id,
    );
    return {
      isLoading: false,
      fiveYears: {
        hasHistory: hasInspectionHistory(dto?.fiveYearsInspectionInfo),
        nextLabel: getNextInspectionLabel(dto?.fiveYearsInspectionInfo),
      },
      sixMonths: {
        hasHistory: hasInspectionHistory(dto?.sixMonthsInspectionInfo),
        nextLabel: getNextInspectionLabel(dto?.sixMonthsInspectionInfo),
      },
    };
  }, [separatorToUpdate, scheduleItem, isScheduleLoading]);

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  const afterModalClose = () => {
    resetForm();
  };

  const invalidateSchedule = () =>
    queryClient.invalidateQueries({
      queryKey: [ScheduledMaintenanceQueryKey.GetOne],
    });

  // CREATE

  const createMutation = useMutation({
    ...createSeparator(),
    onSuccess: (s: Separator) => {
      if (onSuccess) {
        onSuccess(s);
      } else {
        queryClient.invalidateQueries({
          queryKey: [FacilityQueryKey.GetOne],
        });
      }
      invalidateSchedule();
      onModalClose();
    },
  });

  // PATCH
  const patchMutation = useMutation({
    ...patchSeparator(),
    onSuccess: (s: Separator) => {
      if (onSuccess) {
        onSuccess(s);
      } else {
        queryClient.invalidateQueries({
          queryKey: [SeparatorQueryKey.GetOne],
        });
      }
      invalidateSchedule();
      onModalClose();
    },
  });

  const toPayload = (
    data: TSeparatorMetadataSchema,
  ): SeparatorMetadataPayload => ({
    ...data,
    initialFemarsKontrollDate: toInitialInspectionDatePayload(
      data.avtalFemarskontroll,
      data.initialFemarsKontrollDate,
      inspectionSchedule.fiveYears.hasHistory,
    ),
    initialHalvarsKontrollDate: toInitialInspectionDatePayload(
      data.avtalHalvarsKontroll,
      data.initialHalvarsKontrollDate,
      inspectionSchedule.sixMonths.hasHistory,
    ),
  });

  const onSubmit = form.handleSubmit((data: TSeparatorMetadataSchema) => {
    const payload = toPayload(data);
    if (separatorToUpdate) {
      //PATCH
      patchMutation.mutate({
        separatorId: separatorToUpdate.id,
        payload,
      });
    } else {
      // CREATE
      if (!facility) {
        throw Error("facility is missing when creating separator");
      }
      createMutation.mutate({ facilityUuid: facility.uuid, payload });
    }
  });

  const selectedSeparatorType = form.watch("type");

  return {
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    onModalClose,
    form,
    onSubmit,
    selectedSeparatorType,
    inspectionSchedule,
    onNewSeparatorModalClose: onModalClose,
    afterClose: afterModalClose,
    isPending: createMutation.isPending || patchMutation.isPending,
  };
};

export type AddSeparatorLogicRetVal = ReturnType<
  typeof useCreateOrUpdateSeparatorLogic
>;
